import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { showToast } from '../utils/toast';

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  timestamp: string;
  type: 'text' | 'image' | 'file';
  isExpert?: boolean;
  read?: boolean;
}

export interface ChatConversation {
  id: string;
  participantId: string;
  participantName: string;
  participantAvatar?: string;
  isExpert?: boolean;
  lastMessage?: ChatMessage;
  unreadCount: number;
  isOnline: boolean;
  lastSeen?: string;
}

export function useChat() {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [messages, setMessages] = useState<{[conversationId: string]: ChatMessage[]}>({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  // Fetch conversations and subscribe to new messages
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Function to fetch conversations
    const fetchConversations = async () => {
      try {
        // Get all direct messages where the user is either sender or receiver
        const { data: messagesData, error: messagesError } = await supabase
          .from('direct_messages')
          .select(`
            id,
            sender_id,
            receiver_id,
            content,
            type,
            file_url,
            created_at,
            read_at,
            sender:profiles!direct_messages_sender_id_fkey(id, full_name, avatar_url, is_expert),
            receiver:profiles!direct_messages_receiver_id_fkey(id, full_name, avatar_url, is_expert)
          `)
          .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
          .order('created_at', { ascending: false });

        if (messagesError) throw messagesError;

        // Process messages to create conversations
        const conversationsMap = new Map<string, ChatConversation>();
        const messagesMap: {[conversationId: string]: ChatMessage[]} = {};

        messagesData.forEach(msg => {
          // Determine if the user is the sender or receiver
          const isSender = msg.sender_id === user.id;
          const otherUser = isSender ? msg.receiver : msg.sender;
          const otherUserId = otherUser.id;
          
          // Create a unique conversation ID based on the two user IDs
          // Sort the IDs to ensure the same conversation ID regardless of who initiated
          const conversationId = [user.id, otherUserId].sort().join('_');
          
          // Create or update the conversation
          if (!conversationsMap.has(conversationId)) {
            conversationsMap.set(conversationId, {
              id: conversationId,
              participantId: otherUserId,
              participantName: otherUser.full_name || 'User',
              participantAvatar: otherUser.avatar_url,
              isExpert: otherUser.is_expert,
              unreadCount: isSender || msg.read_at ? 0 : 1,
              isOnline: false, // We'll update this with presence data later
              lastSeen: new Date().toISOString() // Placeholder
            });
            messagesMap[conversationId] = [];
          } else if (!isSender && !msg.read_at) {
            // Increment unread count for messages sent to the user that haven't been read
            const conv = conversationsMap.get(conversationId)!;
            conv.unreadCount += 1;
            conversationsMap.set(conversationId, conv);
          }
          
          // Add message to the conversation
          const message: ChatMessage = {
            id: msg.id,
            senderId: msg.sender_id,
            senderName: msg.sender.full_name || 'User',
            senderAvatar: msg.sender.avatar_url,
            content: msg.content,
            timestamp: msg.created_at,
            type: msg.type as 'text' | 'image' | 'file',
            isExpert: msg.sender.is_expert,
            read: !!msg.read_at
          };
          
          messagesMap[conversationId].push(message);
          
          // Update last message in conversation
          const conv = conversationsMap.get(conversationId)!;
          if (!conv.lastMessage || new Date(message.timestamp) > new Date(conv.lastMessage.timestamp)) {
            conv.lastMessage = message;
            conversationsMap.set(conversationId, conv);
          }
        });
        
        // Sort messages by timestamp
        Object.keys(messagesMap).forEach(convId => {
          messagesMap[convId].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        });
        
        // Convert map to array and sort by last message timestamp (descending)
        const sortedConversations = Array.from(conversationsMap.values())
          .sort((a, b) => {
            const timeA = a.lastMessage ? new Date(a.lastMessage.timestamp).getTime() : 0;
            const timeB = b.lastMessage ? new Date(b.lastMessage.timestamp).getTime() : 0;
            return timeB - timeA;
          });
        
        setConversations(sortedConversations);
        setMessages(messagesMap);
      } catch (error) {
        console.error('Error fetching conversations:', error);
        showToast.system.error('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();

    // Subscribe to new messages
    const messageSubscription = supabase
      .channel('direct_messages_channel')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `sender_id=eq.${user.id},receiver_id=eq.${user.id}`
      }, async (payload) => {
        // Fetch the complete message with sender and receiver info
        const { data: newMessage, error } = await supabase
          .from('direct_messages')
          .select(`
            id,
            sender_id,
            receiver_id,
            content,
            type,
            file_url,
            created_at,
            read_at,
            sender:profiles!direct_messages_sender_id_fkey(id, full_name, avatar_url, is_expert),
            receiver:profiles!direct_messages_receiver_id_fkey(id, full_name, avatar_url, is_expert)
          `)
          .eq('id', payload.new.id)
          .single();

        if (error) {
          console.error('Error fetching new message:', error);
          return;
        }

        // Process the new message
        const isSender = newMessage.sender_id === user.id;
        const otherUser = isSender ? newMessage.receiver : newMessage.sender;
        const otherUserId = otherUser.id;
        
        // Create a unique conversation ID
        const conversationId = [user.id, otherUserId].sort().join('_');
        
        // Create the message object
        const messageObj: ChatMessage = {
          id: newMessage.id,
          senderId: newMessage.sender_id,
          senderName: newMessage.sender.full_name || 'User',
          senderAvatar: newMessage.sender.avatar_url,
          content: newMessage.content,
          timestamp: newMessage.created_at,
          type: newMessage.type as 'text' | 'image' | 'file',
          isExpert: newMessage.sender.is_expert,
          read: !!newMessage.read_at
        };
        
        // Update messages state
        setMessages(prev => {
          const updatedMessages = { ...prev };
          if (!updatedMessages[conversationId]) {
            updatedMessages[conversationId] = [];
          }
          updatedMessages[conversationId] = [...updatedMessages[conversationId], messageObj];
          return updatedMessages;
        });
        
        // Update conversations state
        setConversations(prev => {
          const existingConvIndex = prev.findIndex(c => c.id === conversationId);
          const updatedConversations = [...prev];
          
          if (existingConvIndex >= 0) {
            // Update existing conversation
            const updatedConv = { ...updatedConversations[existingConvIndex] };
            updatedConv.lastMessage = messageObj;
            
            // Increment unread count if the user is the receiver and message is unread
            if (!isSender && !newMessage.read_at) {
              updatedConv.unreadCount += 1;
            }
            
            // Move conversation to the top
            updatedConversations.splice(existingConvIndex, 1);
            updatedConversations.unshift(updatedConv);
          } else {
            // Create new conversation
            const newConv: ChatConversation = {
              id: conversationId,
              participantId: otherUserId,
              participantName: otherUser.full_name || 'User',
              participantAvatar: otherUser.avatar_url,
              isExpert: otherUser.is_expert,
              lastMessage: messageObj,
              unreadCount: isSender || newMessage.read_at ? 0 : 1,
              isOnline: false,
              lastSeen: new Date().toISOString()
            };
            
            updatedConversations.unshift(newConv);
          }
          
          return updatedConversations;
        });
        
        // Show notification if the user is the receiver
        if (!isSender) {
          const senderName = newMessage.sender.full_name || 'Someone';
          if (newMessage.sender.is_expert) {
            showToast.chat.expertMessageReceived(senderName);
          } else {
            showToast.chat.messageReceived(senderName);
          }
        }
      })
      .subscribe();

    // Subscribe to notifications
    const notificationSubscription = supabase
      .channel('notifications_channel')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        // Handle new notification
        const notification = payload.new;
        
        // Show toast based on notification type
        switch (notification.type) {
          case 'new_message':
            // Already handled by the message subscription
            break;
          case 'post_reaction':
            showToast.community.reactionAdded(notification.title);
            break;
          case 'expert_answer':
            showToast.expert.questionSubmitted();
            break;
          case 'new_follower':
            showToast.community.followSuccess(notification.title.split(' ')[0]);
            break;
          case 'session_reminder':
            showToast.expert.sessionReminder(notification.title, '15 minutes');
            break;
          case 'order_update':
            showToast.shop.orderShipped(notification.title.split('#')[1]);
            break;
          default:
            showToast.system.info(notification.message);
        }
      })
      .subscribe();

    // Cleanup subscriptions
    return () => {
      supabase.removeChannel(messageSubscription);
      supabase.removeChannel(notificationSubscription);
    };
  }, [user]);

  const sendMessage = async (conversationId: string, content: string, type: 'text' | 'image' | 'file' = 'text', fileUrl?: string) => {
    if (!user || !content.trim()) return;

    try {
      // Parse the conversation ID to get the participant ID
      const [id1, id2] = conversationId.split('_');
      const receiverId = id1 === user.id ? id2 : id1;
      
      // Insert the message
      const { data, error } = await supabase
        .from('direct_messages')
        .insert({
          sender_id: user.id,
          receiver_id: receiverId,
          content: type === 'text' ? content.trim() : content,
          type,
          file_url: fileUrl
        })
        .select(`
          id,
          sender_id,
          receiver_id,
          content,
          type,
          file_url,
          created_at,
          read_at
        `)
        .single();

      if (error) throw error;
      
      // The message will be added to the state via the subscription
      showToast.chat.messageSent();
      
      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      showToast.system.error('Failed to send message');
      throw error;
    }
  };

  const markAsRead = async (conversationId: string) => {
    if (!user) return;

    try {
      // Parse the conversation ID to get the participant ID
      const [id1, id2] = conversationId.split('_');
      const participantId = id1 === user.id ? id2 : id1;
      
      // Update all unread messages from this participant
      const { error } = await supabase
        .from('direct_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('sender_id', participantId)
        .eq('receiver_id', user.id)
        .is('read_at', null);

      if (error) throw error;
      
      // Update local state
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unreadCount: 0 }
          : conv
      ));
      
      // Update messages state
      setMessages(prev => {
        const updatedMessages = { ...prev };
        if (updatedMessages[conversationId]) {
          updatedMessages[conversationId] = updatedMessages[conversationId].map(msg => 
            msg.senderId === participantId && !msg.read
              ? { ...msg, read: true }
              : msg
          );
        }
        return updatedMessages;
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const startConversation = async (participantId: string, participantName: string, isExpert: boolean = false) => {
    if (!user) {
      showToast.auth.authError('Please sign in to start a conversation');
      throw new Error('Not authenticated');
    }

    try {
      // Create a unique conversation ID
      const conversationId = [user.id, participantId].sort().join('_');
      
      // Check if conversation already exists
      const existingConv = conversations.find(c => c.id === conversationId);
      if (existingConv) {
        return conversationId;
      }
      
      // Create a new conversation in state
      const newConversation: ChatConversation = {
        id: conversationId,
        participantId,
        participantName,
        isExpert,
        unreadCount: 0,
        isOnline: false,
        lastSeen: new Date().toISOString()
      };
      
      setConversations(prev => [newConversation, ...prev]);
      setMessages(prev => ({ ...prev, [conversationId]: [] }));
      
      showToast.chat.conversationStarted(participantName);
      
      return conversationId;
    } catch (error) {
      console.error('Error starting conversation:', error);
      showToast.system.error('Failed to start conversation');
      throw error;
    }
  };

  return {
    conversations,
    messages,
    loading,
    sendMessage,
    markAsRead,
    startConversation
  };
}